import { useRef, useState } from 'react';
import { normalizeImageUrl } from '../../utils/utils';
import { useImageRotation } from '../../hooks/useImageRotation';
import Loader from '../Loader/Loader';
import './rotatableImage.css';

interface RotatableImageProps {
  imageUrl: string | null;
  alt: string;
  photoType: string;
  entityId: string;
  endpoint: string;
  className?: string;
  style?: React.CSSProperties;
  initialRotation?: number;
  showControls?: boolean;
  onRotateSuccess?: () => void;
}

/**
 * Компонент для отображения изображения с возможностью поворота
 */
export const RotatableImage: React.FC<RotatableImageProps> = ({
  imageUrl,
  alt,
  photoType,
  entityId,
  endpoint,
  className = '',
  style = {},
  initialRotation = 0,
  showControls = true,
  onRotateSuccess,
}) => {
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const placeholderImage = '/images/profile.png';

  // Используем наш хук
  const { rotation, isRotating, rotateImage, setRotation } = useImageRotation(onRotateSuccess);

  // Устанавливаем начальный угол поворота, если он передан
  useState(() => {
    if (initialRotation !== 0) {
      setRotation(initialRotation);
    }
  });

  const handleRotate = (direction: 'left' | 'right') => {
    if (imageUrl) {
      rotateImage(direction, imageUrl, photoType, entityId, endpoint);
    }
  };

  // Определяем источник изображения с учетом placeholder

  // normalizeImageUrl() passes an absolute (presigned S3) URL through untouched
  const imageSrc = imageUrl ? normalizeImageUrl(imageUrl) : placeholderImage;

  return (
    <div className={` rotatable-image-container ${className}`} style={style}>
      {isImageLoading && <Loader isLoading={true} relative={true} />}
      <img
        ref={imageRef}
        src={imageSrc}
        alt={alt}
        className="rotatable-image"
        onLoad={() => {
          setIsImageLoading(false);
          setHasError(false);
        }}
        onError={() => {
          setIsImageLoading(false);
          setHasError(true);
          // При ошибке загрузки заменяем на placeholder
          if (imageRef.current) {
            imageRef.current.src = placeholderImage;
          }
        }}
        style={{
          ...(isImageLoading ? { display: 'none' } : {}),
          transform: `rotate(${rotation}deg)`,
          transition: 'transform 0.3s ease',
        }}
      />

      {/* Кнопки поворота показываем только если есть изображение и нет ошибки */}
      {imageUrl && !hasError && showControls && (
        <div className={`rotation-controls`}>
          <button
            className={`rotate-button`}
            onClick={() => handleRotate('left')}
            disabled={isRotating}
            title="Повернуть влево"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 10L4 15L9 20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path
                d="M20 4V11C20 12.0609 19.5786 13.0783 18.8284 13.8284C18.0783 14.5786 17.0609 15 16 15H4"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            className={`rotate-button`}
            onClick={() => handleRotate('right')}
            disabled={isRotating}
            title="Повернуть вправо"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M15 10L20 15L15 20"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4 4V11C4 12.0609 4.42143 13.0783 5.17157 13.8284C5.92172 14.5786 6.93913 15 8 15H20"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};
